import { Badge, Flex, Text } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import { Tooltip } from '@/shared/ui/chakra/tooltip';
import type { ShellNavItem } from './types';

/* One nav row, shared by the desktop sidebar (where `collapsed` can hide the label behind a
   Tooltip) and the mobile drawer (always rendered with `collapsed={false}`, plus `onNavigate` to
   close the drawer after a tap). Visual output at `collapsed=false` matches the sidebar's
   pre-shell-refactor markup byte-for-byte — this is what AppShell.tsx's old inline `NavLink`
   looked like, just parameterized over `ShellNavItem` instead of loose props. */

interface ShellNavLinkProps {
  item: ShellNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

export function ShellNavLink({ item, collapsed, onNavigate }: ShellNavLinkProps) {
  const Icon = item.icon;
  const badge = item.badge ?? 0;

  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-label={collapsed ? item.label : undefined}
      style={{ textDecoration: 'none' }}
      activeOptions={{ exact: item.exact }}
    >
      {({ isActive }) => (
        <Flex
          align="center"
          justify={collapsed ? 'center' : 'flex-start'}
          gap={collapsed ? '0' : '3'}
          px="3"
          py="2"
          rounded="md"
          fontSize="sm"
          fontWeight={isActive ? 'semibold' : 'normal'}
          color={isActive ? 'colorPalette.fg' : 'fg'}
          bg={isActive ? 'colorPalette.subtle' : 'transparent'}
          transition="background 0.12s"
          _hover={{ bg: isActive ? 'colorPalette.subtle' : 'bg.muted' }}
          position="relative"
        >
          <Icon size={18} />
          {!collapsed && <Text>{item.label}</Text>}
          {badge > 0 ? (
            collapsed ? (
              <Badge
                position="absolute"
                top="-2px"
                right="-2px"
                colorPalette="red"
                borderRadius="full"
                fontSize="0.6rem"
                minW="16px"
                textAlign="center"
                px="1"
              >
                {badge > 99 ? '99+' : badge}
              </Badge>
            ) : (
              <Badge ml="auto" colorPalette="red" borderRadius="full" fontSize="0.7rem">
                {badge > 99 ? '99+' : badge}
              </Badge>
            )
          ) : null}
        </Flex>
      )}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip content={item.label} positioning={{ placement: 'right' }}>
        {link}
      </Tooltip>
    );
  }
  return link;
}
